import { PAGE_BG_STYLE } from "@/lib/pageBg";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Bot, Send, MessageCircle, Mail, HelpCircle } from "lucide-react";
import AppHeader from "@/components/AppHeader";
import Footer from "@/components/Footer";
import { PageHero } from "@/components/PageHero";


// Complete knowledge base for Flow Marketplace - NFT Platform
const knowledgeBase: Record<string, string> = {
  // WHAT IS FLOW
  "flow": `**What is Flow Marketplace?**

Flow is a decentralized NFT marketplace powered by Solana blockchain and EMON token.

**Key Features:**
• Create NFTs for free (only gas fees)
• Buy and sell digital art
• Earn EMON from your creations
• Bonding curve pricing for fair markets
• AI-powered image verification
• Secure on-chain transactions

**How it works:**
1. Connect your Solana wallet
2. Create or browse NFTs
3. Trade using EMON tokens
4. Earn from sales and resales`,

  // NFT CREATION
  "nft": `**How to create an NFT:**

1. Connect your Solana wallet (Phantom, Solflare, or Backpack)
2. Go to Dashboard
3. Click "Create NFT"
4. Upload your image (JPG, PNG, WebP - max 5MB)
5. Add name and description (optional)
6. Your NFT is created and listed automatically!

**Automatic features:**
• Display name generated from wallet (last 5 chars) if not set
• Base price set automatically
• Image verified by AI within minutes

**Daily limits:**
• Maximum 10 NFTs per day per wallet
• Limit resets at midnight UTC`,

  // BUYING NFTs
  "buy": `**How to buy an NFT:**

1. Browse the Marketplace (Home page)
2. Use Trending, Top Earners, or Recent tabs
3. Search by name, owner, or wallet address
4. Click on an NFT card to see details
5. Click "Buy" and confirm in your wallet
6. NFT ownership transfers instantly!

**Price dynamics (Bonding Curve):**
• Each purchase increases the NFT price
• Price formula: base_price × (1 + sales_count × multiplier)
• Early buyers get better prices
• Previous owners earn from every resale`,

  // SELLING NFTs
  "sell": `**Selling your NFTs:**

Your NFTs are automatically listed when created.

**How earnings work:**
• When someone buys your NFT, you earn EMON
• Earnings = sale price (minus platform fee)
• Earnings added to your "Available" balance
• Withdraw anytime from Dashboard

**Managing listings:**
• View all your NFTs in Dashboard
• Unlist NFTs to remove from marketplace
• Relist anytime to make them available again
• Unlisted NFTs are still owned by you`,

  // WALLET
  "wallet": `**Supported Solana wallets:**

**Desktop browsers:**
• Phantom (recommended) - phantom.app
• Solflare - solflare.com
• Backpack - backpack.app

**Mobile devices:**
• Phantom iOS/Android
• Solflare Mobile
• Backpack Mobile

**How to connect:**
1. Click "Connect Wallet" button
2. Choose your wallet from the list
3. Approve the connection request
4. Sign the authentication message
5. You're connected!

**Requirements:**
• Small amount of SOL for transaction fees
• EMON tokens for buying NFTs`,

  // EMON TOKEN
  "emon": `**EMON Token - Platform Currency**

**What is EMON:**
• Native utility token of Flow Marketplace
• Built on Solana blockchain
• Used for all NFT transactions
• Earned through sales

**How to get EMON:**
• Create and sell NFTs
• Earn from NFT resales
• Trade on supported exchanges

**Your EMON balance:**
• Total Earned: lifetime earnings
• Available: ready to withdraw
• Withdrawn: already claimed

**Price display:**
• All prices shown in EMON
• USD equivalent shown for reference
• Rate updates automatically from Jupiter`,

  // DASHBOARD
  "dashboard": `**Your Dashboard - Control Center**

**Profile section:**
• Set custom display name
• Upload profile avatar
• View your wallet address
• See earned badges

**Balance overview:**
• Total USD earned (lifetime)
• Available balance (withdrawable)
• Already withdrawn amount

**Your NFTs:**
• All NFTs you created
• Current price of each
• Sales count and earnings
• List/unlist toggle

**Actions available:**
• Create new NFT
• Edit display name
• Upload/change avatar
• Request withdrawal
• View notifications`,

  // MARKETPLACE
  "marketplace": `**Marketplace - Browse & Discover**

**Home page sections:**
• Hero with platform stats
• Trending NFTs (most activity)
• Top Earners (highest volume)
• Recently Created
• Search functionality

**Search & Filter:**
• Search by NFT name
• Search by owner name
• Search by wallet address
• Sort by price, date, popularity

**NFT Cards show:**
• NFT image
• Name and owner
• Current price in EMON
• USD equivalent
• Sales count`,

  // VERIFICATION
  "verification": `**NFT Image Verification**

**Automatic AI scanning:**
Every uploaded image is checked by AI for:
• Inappropriate content (adult, violence)
• Potential copyright violations
• Duplicate detection

**Verification status:**
• ✅ Approved - NFT is live and tradeable
• ⏳ Pending - Being verified (1-5 minutes)
• ❌ Rejected - Violates guidelines

**If your NFT is rejected:**
• You receive a notification
• NFT is automatically unlisted
• Counts as 1 strike on your account
• You can create a new NFT with appropriate content

**Guidelines:**
• Original artwork only
• No explicit or violent content
• No copyrighted material
• High quality images recommended`,

  // PRICING
  "price": `**NFT Pricing - Bonding Curve**

**How prices work:**
• Creator sets initial base price
• Each sale increases the price
• Formula ensures fair market dynamics
• No sudden price manipulation possible

**Bonding curve benefits:**
• Early supporters get better prices
• Creators earn from EVERY sale
• Price grows with popularity
• Transparent and predictable

**Example:**
• Base price: 100 EMON
• After 1 sale: ~110 EMON
• After 10 sales: ~200 EMON
• Multiplier varies per NFT`,

  // WITHDRAWAL
  "withdrawal": `**Withdrawing your EMON**

**How to withdraw:**
1. Go to Dashboard
2. Check your "Available" balance
3. Click "Request Withdrawal"
4. Enter amount to withdraw
5. Confirm and wait for verification
6. EMON sent to your wallet!

**Anti-Fraud Protection:**
⚠️ All withdrawals are verified by our security system.
• Most withdrawals are approved instantly
• Some may require additional verification
• Suspicious activity may cause delays

**If your withdrawal is delayed:**
• Check notifications for any alerts
• Contact AI Support for status
• Email support@flowmarketplace.com for appeals
❌ Do NOT contact us on X/Twitter

**Limits:**
• Maximum 5 withdrawal requests per day
• Minimum withdrawal amount applies`,

  // BADGES
  "badge": `**Official FLOW Badges (6 Total)**

⭐ **Early Creator** - First 10 creators on the platform (LIMITED)
🚀 **Rising Creator** - Created 3+ approved NFTs
🔥 **Power Minter** - Created 10+ NFTs
💎 **Top Seller** - #1 seller on the platform
🔗 **Top Sharer** - Most active content sharer
❤️ **Most Liked** - Most loved creator

**Badge Rules:**
• Badges are non-transferable NFTs
• Automatically awarded when you qualify
• Early Creator is LIMITED - only first 10 wallets
• Rising Creator stays as long as you have 3+ NFTs
• Only ONE user can hold ranking badges (Top Seller, Top Sharer, Most Liked)
• Visible on your public profile
• Badge can be lost if requirements change`,

  // ACCOUNT
  "account": `**Account & Profile**

**Your profile includes:**
• Display name (customizable)
• Profile avatar (uploadable)
• Wallet address (fixed)
• Achievement badges
• Created NFTs

**Setting up profile:**
1. Go to Dashboard
2. Click on your name to edit
3. Upload avatar image
4. Changes save automatically

**Display name:**
• Default: last 5 chars of wallet
• Can be customized anytime
• Visible to other users
• Shown on your NFTs`,

  // NOTIFICATIONS
  "notification": `**Notifications System**

**You get notified when:**
• Your NFT is approved/rejected
• Someone buys your NFT
• You reach a sales milestone
• You earn a new badge
• Important account updates

**Viewing notifications:**
• Bell icon in header (when logged in)
• Red dot shows unread count
• Click to see all notifications
• Mark as read individually

**Notification types:**
• 🎉 Sale notifications
• ✅ NFT approved
• ❌ NFT rejected
• 🏆 Badge earned
• ⚠️ Account warnings`,

  // SECURITY & ANTI-FRAUD
  "security": `**Platform Security & Anti-Fraud**

**Your protection:**
• Non-custodial: we never hold your keys
• All transactions signed by YOU only
• On-chain verification for everything

**Anti-Fraud System:**
• Advanced multi-layer fraud detection
• All transactions monitored in real-time
• Suspicious activity automatically flagged
• Withdrawals verified before processing

⚠️ **We do NOT reveal how our anti-fraud system works** to prevent exploitation.

**Account Restrictions:**
• Accounts may be temporarily restricted during security reviews
• Permanent bans for confirmed fraud
• All assets may be frozen if fraud is detected

**If you believe you were incorrectly flagged:**
📧 Contact support via email: support@flowmarketplace.com
❌ Do NOT contact us on X/Twitter — NO support is provided there`,

  // BAN AND RESTRICTIONS
  "ban": `**Account Bans & Restrictions**

**Types of restrictions:**
• **Strike System:** 3 strikes = temporary ban (image policy violations)
• **Fraud Ban:** Permanent ban for fraudulent activity
• **Frozen Account:** Assets locked pending investigation

**Why accounts get banned:**
• Uploading copyrighted/inappropriate images
• Wash trading or market manipulation
• Using multiple accounts to self-trade
• Any form of fraudulent activity

**What happens when banned:**
• Cannot create new NFTs
• Cannot request withdrawals
• Assets may be frozen
• Notifications sent to your account

**Appeal Process:**
1. Go to AI Support (this page)
2. Explain your situation
3. Request email escalation to human team
4. Team reviews within 48-72 hours

📧 **Email only:** support@flowmarketplace.com
❌ **We do NOT provide support on X/Twitter**`,

  // WITHDRAWAL ISSUES
  "withdraw_help": `**Withdrawal Issues & Delays**

**Common reasons for delays:**
• Security verification in progress
• Account flagged for review
• Unusual activity detected
• New account with large withdrawal

**What to do:**
1. Check your notification bell for alerts
2. Verify your account is not restricted
3. Contact AI Support if issue persists
4. Request email escalation if needed

**Withdrawal blocked?**
Your account may be under security review. This is to protect you and the platform from fraud.

**How to resolve:**
• Wait 24-48 hours for automatic review
• If still blocked, contact AI Support
• Provide any requested verification
• Our team will review your case

📧 **Email support:** support@flowmarketplace.com
❌ **No support on X/Twitter - messages will be ignored**`,

  // CONTACT SUPPORT
  "contact": `**How to Get Help**

**AI Support (Recommended):**
• Available 24/7 on this page
• Instant answers to common questions
• Can escalate to human team

**Email Support:**
📧 support@flowmarketplace.com
• For complex issues
• Account appeals
• Fraud investigations
• Response within 48-72 hours

**⚠️ IMPORTANT:**
❌ We do NOT provide support on X/Twitter
❌ Messages on social media will be ignored
❌ Never share private keys with anyone

**For urgent account issues:**
1. Describe your problem to AI Support
2. Include your wallet address
3. Request email escalation
4. Our team will contact you`,

  // TROUBLESHOOTING
  "problem": `**Common Issues & Solutions**

**"Cannot create NFT"**
→ Check wallet is connected
→ Verify you have SOL for fees
→ Check daily limit (10/day)
→ Make sure account isn't frozen

**"Transaction failed"**
→ Check SOL balance for gas fees
→ Verify EMON balance for purchases
→ Try reconnecting your wallet
→ Refresh the page and retry

**"NFT not showing"**
→ Wait for verification (1-5 min)
→ Check if it was rejected
→ Refresh the marketplace page

**"Cannot withdraw"**
→ Check available balance > 0
→ Max 3 withdrawals per day
→ Ensure minimum amount met`,

  // FEES
  "fee": `**Platform Fees**

**Transaction fees:**
• Small platform fee on sales
• Solana network fees (gas)
• Fees shown before confirmation

**What you pay:**
• Creating NFT: only SOL gas (~0.001 SOL)
• Buying NFT: EMON price + gas
• Selling NFT: platform takes small %

**Transparency:**
• All fees visible upfront
• No hidden charges
• On-chain and verifiable`,

  // SOLANA
  "solana": `**Solana Blockchain**

**Why Solana:**
• Fast transactions (400ms)
• Low fees (~$0.001)
• Environmentally friendly
• Large ecosystem

**What you need:**
• Solana wallet (Phantom, etc.)
• Small amount of SOL for fees
• EMON tokens for trading

**Viewing transactions:**
• Click Solana Explorer links
• Verify on solscan.io
• All transactions public
• Smart contract verified`,

  // GETTING STARTED
  "start": `**Getting Started Guide**

**Step 1: Get a wallet**
• Download Phantom, Solflare, or Backpack
• Create a new wallet
• Save your recovery phrase safely!

**Step 2: Get SOL**
• Buy SOL on an exchange
• Transfer to your wallet
• Need ~0.01 SOL for fees

**Step 3: Connect to Flow**
• Visit flowmarketplace.com
• Click "Connect Wallet"
• Approve the connection

**Step 4: Start creating!**
• Go to Dashboard
• Click "Create NFT"
• Upload your art
• Start earning EMON!`,
};

// Keywords to knowledge mapping
const keywordMap: Record<string, string[]> = {
  "flow": ["flow", "what is", "about", "platform", "marketplace", "how does it work"],
  "nft": ["nft", "create", "mint", "upload", "image", "make nft", "new nft"],
  "buy": ["buy", "purchase", "acquire", "get nft", "buying"],
  "sell": ["sell", "list", "earnings", "profit", "selling", "revenue"],
  "wallet": ["wallet", "phantom", "solflare", "backpack", "connect", "connection"],
  "emon": ["emon", "token", "coin", "crypto", "currency", "money"],
  "dashboard": ["dashboard", "my account", "my nfts", "control"],
  "marketplace": ["marketplace", "browse", "discover", "explore", "home", "trending"],
  "verification": ["verify", "verification", "pending", "rejected", "approved", "check", "ai scan"],
  "price": ["price", "cost", "bonding", "curve", "pricing", "value"],
  "withdrawal": ["withdraw", "withdrawal", "cash out", "claim", "payout"],
  "badge": ["badge", "achievement", "level", "reward", "trophy"],
  "account": ["account", "profile", "display name", "avatar", "settings"],
  "notification": ["notification", "notify", "alert", "bell", "updates"],
  "security": ["security", "safe", "protect", "fraud", "antifraud", "anti-fraud", "scam"],
  "ban": ["ban", "banned", "suspended", "frozen", "restricted", "blocked", "strike", "appeal"],
  "withdraw_help": ["withdrawal delayed", "cannot withdraw", "withdrawal blocked", "withdrawal issue", "withdrawal problem"],
  "contact": ["contact", "support", "help", "email", "human", "team", "escalate", "twitter", "x"],
  "problem": ["problem", "error", "not working", "bug", "issue", "failed", "cannot", "trouble"],
  "fee": ["fee", "fees", "commission", "charge", "cost"],
  "solana": ["solana", "sol", "blockchain", "chain", "explorer"],
  "start": ["start", "begin", "getting started", "new user", "first time", "how to start"],
};

function findAnswer(query: string): string {
  const q = query.toLowerCase();

  // Check each keyword category
  for (const [category, keywords] of Object.entries(keywordMap)) {
    for (const keyword of keywords) {
      if (q.includes(keyword)) {
        return knowledgeBase[category];
      }
    }
  }

  // Default response
  return `I can help you with Flow Marketplace! Try asking about:

• What is Flow Marketplace?
• How to create an NFT?
• How to buy or sell NFTs?
• Wallet connection help
• EMON token and earnings
• Withdrawing your earnings
• NFT verification process
• Pricing and bonding curve
• Badges and achievements
• Account settings

Or describe your issue in more detail!`;
}

export default function SupportPage() {
  const [query, setQuery] = useState("");
  const [history, setHistory] = useState<{ q: string; a: string }[]>([]);

  const handleAsk = () => {
    if (!query.trim()) return;
    const answer = findAnswer(query);
    setHistory([{ q: query, a: answer }, ...history.slice(0, 4)]);
    setQuery("");
  };

  return (
    <div className="min-h-screen relative flex flex-col" style={PAGE_BG_STYLE}>
      <AppHeader currentPage="support" />

      <main className="flex-1 pt-20 pb-24 px-4">
        <div className="max-w-7xl mx-auto space-y-4">
          <PageHero
            title="Flow Support"
            subtitle="Ask anything about the platform — our AI assistant knows every detail!"
          />

          {/* AI Chat Card */}
          <Card className="bg-gradient-to-br from-primary/20 via-primary/10 to-accent/5 border-primary/30 hover:border-primary/50 transition-all duration-300 overflow-hidden relative backdrop-blur-sm mb-8">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,hsl(var(--primary)/0.15),transparent_50%)]" />
            <CardContent className="p-0 relative">
              {/* Chat header */}
              <div className="p-4 border-b border-border/30">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg gradient-primary shadow-lg shadow-primary/30">
                    <Bot className="h-5 w-5 text-primary-foreground" />
                  </div>
                  <div>
                    <span className="text-base font-bold text-foreground">Flow AI Assistant</span>
                    <p className="text-xs text-muted-foreground">
                      Expert on NFTs, wallet, EMON, and all platform features
                    </p>
                  </div>
                </div>
              </div>

              {/* Chat content */}
              <div className="p-4 max-h-96 overflow-y-auto text-sm font-normal">
                {history.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <div className="p-4 rounded-full bg-primary/20 w-fit mx-auto mb-4">
                      <MessageCircle className="h-8 w-8 text-primary opacity-70" />
                    </div>
                    <p className="text-sm mb-4">Type your question to get started</p>
                    <div className="flex flex-wrap gap-2 justify-center">
                      {["What is Flow?", "Create NFT", "Buy NFT", "Withdraw", "Wallet help"].map((suggestion) => (
                        <Button
                          key={suggestion}
                          variant="outline"
                          size="sm"
                          className="text-xs hover:bg-primary/10 hover:border-primary/50 transition-all"
                          onClick={() => setQuery(suggestion)}
                        >
                          {suggestion}
                        </Button>
                      ))}
                    </div>
                  </div>
                ) : (
                  history.map((item, i) => (
                    <div key={i} className="mb-4">
                      <div className="flex items-start gap-2 mb-2">
                        <span className="text-xs bg-primary/20 text-primary px-2 py-0.5 rounded flex-shrink-0">You</span>
                        <span className="text-sm text-foreground">{item.q}</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <span className="text-xs bg-accent/20 text-accent px-2 py-0.5 rounded flex-shrink-0">AI</span>
                        <div className="text-sm text-muted-foreground whitespace-pre-wrap">{item.a}</div>
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Chat input */}
              <div className="p-4 border-t border-border/30 bg-card/50">
                <div className="flex gap-2">
                  <Input
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleAsk()}
                    placeholder="Ask about NFTs, wallet, EMON, trading..."
                    className="flex-1 bg-background/50 border-border/50 focus:border-primary/50"
                  />
                  <Button
                    onClick={handleAsk}
                    disabled={!query.trim()}
                    className="bg-gradient-to-r from-primary to-accent hover:opacity-90 transition-opacity"
                  >
                    <Send className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Contact Card */}
          <Card className="bg-gradient-to-br from-accent/10 via-card to-card border-accent/20 hover:border-accent/40 transition-all duration-300 backdrop-blur-sm">
            <CardContent className="p-6">
              <div className="flex flex-col sm:flex-row gap-4 sm:items-start overflow-hidden">
                <div className="p-3 rounded-xl bg-gradient-to-br from-accent to-primary flex-shrink-0">
                  <HelpCircle className="h-5 w-5 text-white" />
                </div>
                <div className="min-w-0 flex-1">
                  <h2 className="text-xl font-semibold text-foreground mb-2">Need More Help?</h2>
                  <p className="text-muted-foreground leading-relaxed mb-3">
                    Can't find the answer you're looking for? Contact our support team directly:
                  </p>
                  <a 
                    href="mailto:support@emonboost.com" 
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-primary/20 hover:bg-primary/30 text-primary font-medium transition-all duration-300 hover:scale-105 text-sm sm:text-base break-all"
                  >
                    <Mail className="h-4 w-4 flex-shrink-0" />
                    <span className="truncate sm:overflow-visible">support@emonboost.com</span>
                  </a>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>

      <Footer />
    </div>
  );
}
